/**
 * Lightweight analytics helper — gated behind cookie consent.
 *
 * Behaviour:
 * - All non-essential events are queued until the user makes a consent choice.
 * - On "accepted": queued events are flushed and future events fire immediately.
 * - On "declined": the queue is dropped and future non-essential events are no-ops.
 * - A small allowlist of essential consent-bookkeeping events
 *   (cookie_consent_grant / cookie_consent_deny / cookie_policy_view) ALWAYS
 *   fires so we can measure consent choices themselves.
 * - Consent state is read from localStorage key `sailkha_cookie_consent`
 *   (written by CookieConsent.tsx), and refreshed on the
 *   `sailkha:cookie-consent-action` window event.
 */

type EventParams = Record<string, unknown>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DataLayerWindow = Window & { dataLayer?: any[]; gtag?: (...args: any[]) => void };

type ConsentState = "accepted" | "declined" | "unknown";
const CONSENT_STORAGE_KEY = "sailkha_cookie_consent";

// Events that ARE allowed to fire even before consent is granted, because
// they describe the consent decision itself (needed for compliance auditing).
const ESSENTIAL_EVENTS = new Set([
  "cookie_consent_grant",
  "cookie_consent_deny",
  "cookie_policy_view",
]);

// In-memory queue while consent is "unknown".
const queue: { event: string; params: EventParams }[] = [];
const MAX_QUEUE = 50;

function readConsent(): ConsentState {
  if (typeof window === "undefined") return "unknown";
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return "unknown";
    const parsed = JSON.parse(raw) as { value?: string };
    if (parsed?.value === "accepted") return "accepted";
    if (parsed?.value === "declined") return "declined";
    return "unknown";
  } catch {
    return "unknown";
  }
}

// Generate a UUID v4 for event deduplication and cross-retry tracing.
// Uses crypto.randomUUID when available, falls back to a manual v4.
function generateEventId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* ignore */ }
  // RFC4122 v4 fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function pushToSinks(eventName: string, params: EventParams): void {
  if (typeof window === "undefined") return;
  const w = window as DataLayerWindow;
  // event_id is added if not already provided by the caller (queued events
  // already carry an id so dedup works across the consent-gate flush).
  const eventId = (params.event_id as string | undefined) ?? generateEventId();
  const payload = {
    event: eventName,
    ...params,
    event_id: eventId,
    event_at: new Date().toISOString(),
  };
  try {
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(payload);
    if (typeof w.gtag === "function") {
      w.gtag("event", eventName, { ...params, event_id: eventId });
    }
    window.dispatchEvent(
      new CustomEvent("sailkha:analytics", { detail: payload })
    );
  } catch {
    /* never let analytics break the UI */
  }
}

function flushQueue(): void {
  while (queue.length) {
    const next = queue.shift();
    if (next) pushToSinks(next.event, next.params);
  }
}

// Listen for consent changes (CookieConsent dispatches this on accept/decline).
if (typeof window !== "undefined") {
  window.addEventListener("sailkha:cookie-consent-action", (e: Event) => {
    const detail = (e as CustomEvent<{ action?: string }>).detail;
    if (detail?.action === "accepted") {
      flushQueue();
    } else if (detail?.action === "declined") {
      queue.length = 0;
    }
  });
}

export function trackEvent(eventName: string, params: EventParams = {}): void {
  if (typeof window === "undefined") return;

  // Stamp an event_id at the call site so the same id is preserved
  // whether the event fires immediately or is replayed from the queue
  // after the user grants consent (enables retry-safe deduplication).
  const paramsWithId: EventParams =
    "event_id" in params ? params : { ...params, event_id: generateEventId() };

  // Essential events bypass the consent gate entirely.
  if (ESSENTIAL_EVENTS.has(eventName)) {
    pushToSinks(eventName, paramsWithId);
    return;
  }

  const consent = readConsent();
  if (consent === "accepted") {
    pushToSinks(eventName, paramsWithId);
    return;
  }
  if (consent === "declined") {
    // Drop silently — user opted out.
    return;
  }
  // "unknown" — queue until consent decision (with a small cap).
  if (queue.length < MAX_QUEUE) {
    queue.push({ event: eventName, params: paramsWithId });
  }
}

// Session-scoped lead attribution. quoteClick stamps the latest CTA
// context (location + optional category) so the eventual form submit can
// attribute the lead to the right source/category without prop drilling.
const LEAD_ATTRIBUTION_KEY = "sailkha_lead_attribution";

export interface LeadAttribution {
  lead_source: string;
  category?: string;
  location?: string;
  ts: number;
}

export function setLeadAttribution(attr: Omit<LeadAttribution, "ts">): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      LEAD_ATTRIBUTION_KEY,
      JSON.stringify({ ...attr, ts: Date.now() })
    );
  } catch { /* ignore */ }
}

export function readLeadAttribution(): LeadAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LEAD_ATTRIBUTION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LeadAttribution;
  } catch {
    return null;
  }
}

// Convenience helpers for the events we care about today.
// All lead-generating helpers consistently emit `lead_source` and (when known)
// `category` so dashboards can attribute every quote.
export const analytics = {
  quoteClick: (location: string, category?: string) => {
    // Stamp attribution so the next form submit knows which CTA / category
    // the user came from.
    setLeadAttribution({
      lead_source: "website_form",
      location,
      category,
    });
    trackEvent("generate_lead", {
      cta: "get_quote",
      lead_source: "website_form",
      location,
      ...(category ? { category } : {}),
    });
  },
  contactSubmit: (interest?: string, extra?: { lead_source?: string; category?: string }) =>
    trackEvent("form_submit", {
      form_id: "contact",
      interest,
      lead_source: extra?.lead_source ?? "website_form",
      ...(extra?.category ? { category: extra.category } : {}),
    }),
  contactSubmitError: (reason: string) =>
    trackEvent("form_submit_error", { form_id: "contact", reason }),
  cookieAccept: () => trackEvent("cookie_consent_grant", { method: "banner" }),
  cookieDecline: () => trackEvent("cookie_consent_deny", { method: "banner" }),
  cookiePrivacyClick: () =>
    trackEvent("cookie_policy_view", { source: "consent_banner" }),
  whatsappFloatClick: (category?: string) =>
    trackEvent("generate_lead", {
      cta: "whatsapp_chat",
      lead_source: "whatsapp_float",
      location: "floating_button",
      ...(category ? { category } : {}),
    }),
  whatsappFloatOpen: (category?: string) =>
    trackEvent("whatsapp_message_open", {
      lead_source: "whatsapp_float",
      location: "floating_button",
      ...(category ? { category } : {}),
    }),
  // Fired when the user actually copies/opens the WhatsApp message from the
  // Thank You page, so follow-up via WhatsApp counts as its own lead source.
  whatsappQuoteSend: (category?: string, location: string = "thank_you") =>
    trackEvent("generate_lead", {
      cta: "whatsapp_quote",
      lead_source: "whatsapp_thankyou",
      location,
      ...(category ? { category } : {}),
    }),
};
